// NeevBot.js — Pure SVG robot avatar with CSS animations for NeevStar
import React, { useState, useEffect, useRef } from 'react';

const css = `
@keyframes nb-idle {
  0%,100% { transform: translateY(0); }
  50%      { transform: translateY(-5px); }
}
@keyframes nb-jump {
  0%   { transform: translateY(0) scale(1); }
  25%  { transform: translateY(-20px) scale(1.1); }
  50%  { transform: translateY(-28px) scale(1.05); }
  75%  { transform: translateY(-10px) scale(1.02); }
  100% { transform: translateY(0) scale(1); }
}
@keyframes nb-shake {
  0%,100% { transform: rotate(0); }
  15%     { transform: rotate(-12deg); }
  30%     { transform: rotate(12deg); }
  45%     { transform: rotate(-8deg); }
  60%     { transform: rotate(8deg); }
  75%     { transform: rotate(-4deg); }
}
@keyframes nb-spin {
  0%   { transform: rotate(0) scale(1); }
  50%  { transform: rotate(180deg) scale(1.15); }
  100% { transform: rotate(360deg) scale(1); }
}
@keyframes nb-wave {
  0%,100% { transform: rotate(0); transform-origin: bottom center; }
  20%     { transform: rotate(-20deg); transform-origin: bottom center; }
  40%     { transform: rotate(20deg); transform-origin: bottom center; }
  60%     { transform: rotate(-15deg); transform-origin: bottom center; }
  80%     { transform: rotate(10deg); transform-origin: bottom center; }
}
@keyframes nb-flash {
  0%,100% { filter: brightness(1); }
  25%     { filter: brightness(2) saturate(2); }
  50%     { filter: brightness(1.5); }
  75%     { filter: brightness(2.5) saturate(3); }
}
@keyframes nb-sparkle {
  0%   { transform: scale(0) rotate(0); opacity: 1; }
  50%  { transform: scale(1.2) rotate(180deg); opacity: 1; }
  100% { transform: scale(0) rotate(360deg); opacity: 0; }
}
@keyframes nb-eyeBlink {
  0%,90%,100% { transform: scaleY(1); }
  95%          { transform: scaleY(0.1); }
}
.nb-idle    { animation: nb-idle 2.2s ease-in-out infinite; }
.nb-jump    { animation: nb-jump 0.6s cubic-bezier(0.34,1.56,0.64,1); }
.nb-shake   { animation: nb-shake 0.6s ease-in-out; }
.nb-spin    { animation: nb-spin 0.8s cubic-bezier(0.34,1.56,0.64,1); }
.nb-wave    { animation: nb-wave 1s ease-in-out; }
.nb-flash   { animation: nb-flash 0.5s ease-in-out; }
.nb-sparkle { animation: nb-sparkle 0.7s ease-out forwards; }
.nb-eye     { animation: nb-eyeBlink 4s ease-in-out infinite; transform-origin: center; }
`;

// Level configs
const LEVELS = [
  { level: 1, minCoins: 0,    name: 'Rookie Bot',     bodyColor: '#607D8B', headColor: '#78909C', eyeColor: '#00E5FF', accent: '#90A4AE', hasBackpack: false, hasBooks: false, hasJetpack: false, hasCrown: false },
  { level: 2, minCoins: 100,  name: 'Blue Bot',       bodyColor: '#1565C0', headColor: '#1976D2', eyeColor: '#00E5FF', accent: '#42A5F5', hasBackpack: true,  hasBooks: false, hasJetpack: false, hasCrown: false },
  { level: 3, minCoins: 300,  name: 'Scholar Bot',    bodyColor: '#00695C', headColor: '#00897B', eyeColor: '#69F0AE', accent: '#26A69A', hasBackpack: false, hasBooks: true,  hasJetpack: false, hasCrown: false },
  { level: 4, minCoins: 600,  name: 'Jet Bot',        bodyColor: '#F57F17', headColor: '#F9A825', eyeColor: '#FFD740', accent: '#FFCC02', hasBackpack: false, hasBooks: false, hasJetpack: true,  hasCrown: false },
  { level: 5, minCoins: 1000, name: 'Legend Bot',     bodyColor: '#880E4F', headColor: '#AD1457', eyeColor: '#F48FB1', accent: '#FFD700', hasBackpack: false, hasBooks: false, hasJetpack: true,  hasCrown: true  },
];

function getLevelConfig(coins) {
  const sorted = [...LEVELS].reverse();
  return sorted.find(l => coins >= l.minCoins) || LEVELS[0];
}

function NeevBotSVG({ config, size = 80, reaction = 'idle' }) {
  const s = size / 80; // scale factor
  const { bodyColor, headColor, eyeColor, accent, hasBackpack, hasBooks, hasJetpack, hasCrown } = config;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 80 80"
      style={{ overflow: 'visible' }}
    >
      {/* Shadow */}
      <ellipse cx="40" cy="76" rx="18" ry="4" fill="rgba(0,0,0,0.15)" />

      {/* Jetpack flames (level 4+) */}
      {hasJetpack && (
        <g>
          <ellipse cx="28" cy="68" rx="4" ry="7" fill="#FF9500" opacity="0.8">
            <animate attributeName="ry" values="7;9;6;8;7" dur="0.4s" repeatCount="indefinite"/>
          </ellipse>
          <ellipse cx="28" cy="68" rx="2" ry="5" fill="#FFD600" opacity="0.9">
            <animate attributeName="ry" values="5;7;4;6;5" dur="0.3s" repeatCount="indefinite"/>
          </ellipse>
          <ellipse cx="52" cy="68" rx="4" ry="7" fill="#FF9500" opacity="0.8">
            <animate attributeName="ry" values="6;8;7;9;6" dur="0.35s" repeatCount="indefinite"/>
          </ellipse>
          <ellipse cx="52" cy="68" rx="2" ry="5" fill="#FFD600" opacity="0.9">
            <animate attributeName="ry" values="4;6;5;7;4" dur="0.28s" repeatCount="indefinite"/>
          </ellipse>
        </g>
      )}

      {/* Backpack (level 2) */}
      {hasBackpack && (
        <rect x="48" y="42" width="12" height="16" rx="3" fill={accent} stroke={bodyColor} strokeWidth="1.5"/>
      )}

      {/* Books (level 3) */}
      {hasBooks && (
        <g>
          <rect x="50" y="44" width="5" height="14" rx="1" fill="#E91E63"/>
          <rect x="56" y="44" width="5" height="14" rx="1" fill="#3F51B5"/>
          <rect x="53" y="43" width="5" height="14" rx="1" fill="#4CAF50"/>
        </g>
      )}

      {/* Jetpack body (level 4+) */}
      {hasJetpack && (
        <g>
          <rect x="50" y="42" width="10" height="18" rx="4" fill={accent} stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
          <rect x="22" y="42" width="10" height="18" rx="4" fill={accent} stroke="rgba(255,255,255,0.3)" strokeWidth="1"/>
        </g>
      )}

      {/* Body */}
      <rect x="22" y="46" width="36" height="24" rx="8" fill={bodyColor} />

      {/* Body detail panel */}
      <rect x="28" y="52" width="24" height="12" rx="4" fill="rgba(0,0,0,0.2)" />
      <circle cx="34" cy="58" r="3" fill={eyeColor} opacity="0.8"/>
      <circle cx="40" cy="58" r="3" fill={accent} opacity="0.7"/>
      <circle cx="46" cy="58" r="3" fill={eyeColor} opacity="0.8"/>

      {/* Arms */}
      <rect x="8" y="48" width="14" height="8" rx="4" fill={bodyColor}/>
      <circle cx="8" cy="52" r="5" fill={headColor}/>
      <rect x="58" y="48" width="14" height="8" rx="4" fill={bodyColor}/>
      <circle cx="72" cy="52" r="5" fill={headColor}/>

      {/* Neck */}
      <rect x="34" y="40" width="12" height="8" rx="3" fill={headColor}/>

      {/* Head */}
      <rect x="16" y="16" width="48" height="28" rx="12" fill={headColor}/>

      {/* Head highlight */}
      <rect x="20" y="18" width="40" height="10" rx="8" fill="rgba(255,255,255,0.12)"/>

      {/* Eyes */}
      <g className="nb-eye" style={{ transformOrigin: '28px 28px' }}>
        <rect x="22" y="24" width="12" height="12" rx="4" fill="rgba(0,0,0,0.3)"/>
        <rect x="23" y="25" width="10" height="10" rx="3" fill={eyeColor}/>
        <circle cx="26" cy="28" r="3" fill="rgba(0,0,0,0.4)"/>
        <circle cx="27" cy="27" r="1" fill="white" opacity="0.8"/>
      </g>
      <g className="nb-eye" style={{ transformOrigin: '52px 28px' }}>
        <rect x="46" y="24" width="12" height="12" rx="4" fill="rgba(0,0,0,0.3)"/>
        <rect x="47" y="25" width="10" height="10" rx="3" fill={eyeColor}/>
        <circle cx="50" cy="28" r="3" fill="rgba(0,0,0,0.4)"/>
        <circle cx="51" cy="27" r="1" fill="white" opacity="0.8"/>
      </g>

      {/* Mouth */}
      <rect x="30" y="38" width="20" height="4" rx="2" fill="rgba(0,0,0,0.25)"/>
      <rect x="31" y="39" width="18" height="2" rx="1" fill={accent} opacity="0.7"/>

      {/* Antenna */}
      <line x1="40" y1="16" x2="40" y2="6" stroke={headColor} strokeWidth="3" strokeLinecap="round"/>
      <circle cx="40" cy="5" r="4" fill={eyeColor}/>
      <circle cx="40" cy="5" r="2" fill="white" opacity="0.6"/>

      {/* Side bolts */}
      <circle cx="17" cy="28" r="2.5" fill={accent} opacity="0.8"/>
      <circle cx="63" cy="28" r="2.5" fill={accent} opacity="0.8"/>

      {/* Crown (level 5) */}
      {hasCrown && (
        <g>
          <polygon points="26,16 30,8 34,14 40,6 46,14 50,8 54,16" fill="#FFD700" stroke="#FFA000" strokeWidth="1"/>
          <circle cx="40" cy="6" r="3" fill="#FF2D55"/>
          <circle cx="30" cy="8" r="2" fill="#00BCD4"/>
          <circle cx="50" cy="8" r="2" fill="#8B2BE2"/>
        </g>
      )}

      {/* Rainbow shine on level 5 */}
      {hasCrown && (
        <rect x="16" y="16" width="48" height="28" rx="12"
          fill="url(#rainbowGrad)" opacity="0.15"/>
      )}

      <defs>
        <linearGradient id="rainbowGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF2D55"/>
          <stop offset="33%" stopColor="#FFD600"/>
          <stop offset="66%" stopColor="#00C851"/>
          <stop offset="100%" stopColor="#0066FF"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

// Exported hook to trigger reactions globally
const listeners = new Set();
export function triggerNeevBotReaction(reaction) {
  listeners.forEach(fn => fn(reaction));
}

export default function NeevBot({ coins = 0, size = 80, showLevel = false }) {
  const [reaction, setReaction] = useState('idle');
  const [sparkles, setSparkles] = useState([]);
  const timeoutRef = useRef(null);
  const config = getLevelConfig(coins);

  useEffect(() => {
    if (!document.getElementById('nb-css')) {
      const s = document.createElement('style');
      s.id = 'nb-css';
      s.textContent = css;
      document.head.appendChild(s);
    }

    const handler = (r) => {
      setReaction(r);
      clearTimeout(timeoutRef.current);
      if (r === 'levelup') {
        setSparkles(Array.from({ length: 6 }, (_, i) => ({
          id: i,
          angle: i * 60,
          dist: 30 + Math.random() * 20,
        })));
        setTimeout(() => setSparkles([]), 800);
      }
      timeoutRef.current = setTimeout(() => setReaction('idle'), 900);
    };

    listeners.add(handler);
    return () => {
      listeners.delete(handler);
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const reactionClass = {
    idle:    'nb-idle',
    correct: 'nb-jump nb-flash',
    wrong:   'nb-shake',
    levelup: 'nb-spin nb-flash',
    login:   'nb-wave',
  }[reaction] || 'nb-idle';

  return (
    <div style={{ position: 'relative', display: 'inline-block', textAlign: 'center' }}>
      <div className={reactionClass} style={{ display: 'inline-block' }}>
        <NeevBotSVG config={config} size={size} reaction={reaction} />
      </div>

      {/* Sparkles on level-up */}
      {sparkles.map(sp => {
        const rad = (sp.angle * Math.PI) / 180;
        const x = sp.dist * Math.cos(rad);
        const y = sp.dist * Math.sin(rad);
        return (
          <div key={sp.id} className="nb-sparkle"
            style={{
              position: 'absolute',
              top: '50%', left: '50%',
              transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
              fontSize: 14, pointerEvents: 'none',
            }}>
            ✨
          </div>
        );
      })}

      {/* Level badge */}
      {showLevel && (
        <div style={{
          marginTop: 4,
          background: 'linear-gradient(135deg,#FFD600,#FF9500)',
          borderRadius: 10, padding: '2px 8px',
          display: 'inline-block',
        }}>
          <span style={{ fontSize: 10, fontWeight: 900, color: '#1A1A2E' }}>
            Lv.{config.level} {config.name}
          </span>
        </div>
      )}
    </div>
  );
}

// Small inline version for home screen corner
export function NeevBotMini({ coins = 0 }) {
  return <NeevBot coins={coins} size={40} showLevel={false} />;
}

// Level progress bar widget
export function NeevBotLevelBar({ coins = 0 }) {
  const config = getLevelConfig(coins);
  const nextLevel = LEVELS.find(l => l.level === config.level + 1);
  const pct = nextLevel
    ? Math.min(100, Math.round(((coins - config.minCoins) / (nextLevel.minCoins - config.minCoins)) * 100))
    : 100;

  return (
    <div style={{
      background: 'white', borderRadius: 16, padding: '12px 16px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.06)', marginBottom: 12,
      fontFamily: "'Nunito', sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <NeevBot coins={coins} size={52} />
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 900, color: '#1A1A2E' }}>
              🤖 {config.name}
            </span>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#888' }}>
              🪙 {coins} coins
            </span>
          </div>
          <div style={{ background: '#F0F0F0', borderRadius: 6, height: 8, overflow: 'hidden' }}>
            <div style={{
              width: `${pct}%`, height: '100%',
              background: 'linear-gradient(90deg,#FFD600,#FF9500)',
              borderRadius: 6, transition: 'width 0.6s ease',
            }} />
          </div>
          <p style={{ fontSize: 11, color: '#AAA', fontWeight: 700, margin: '3px 0 0' }}>
            {nextLevel
              ? `${nextLevel.minCoins - coins} coins to Lv.${nextLevel.level} — ${nextLevel.name}`
              : '🏆 Max Level Reached!'}
          </p>
        </div>
      </div>
    </div>
  );
}
